import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Camera, ImagePlus, Store, MapPin, LocateFixed, Phone, Globe,
  Instagram, Music2, Clock, Save, Tag, Loader2, ToggleLeft, ToggleRight, Trash2,
  Utensils, Package,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { CATEGORY_OPTIONS, WEEKDAYS, isPresetCategory, slugifyCategory, formatCategoryLabel } from "../lib/categories";
import CategoryIcon from "../components/CategoryIcon";

function blankHours() {
  return WEEKDAYS.reduce((acc, d) => {
    acc[d.key] = { open: "", close: "", closed: false };
    return acc;
  }, {});
}

function Section({ title, subtitle, children, testid }) {
  return (
    <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5 space-y-4" data-testid={testid}>
      <div>
        <h2 className="text-lg font-black tracking-tight">{title}</h2>
        {subtitle && <p className="text-xs text-zinc-400 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

export default function VendorShopEdit() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [busyLoc, setBusyLoc] = useState(false);
  const [customTagInput, setCustomTagInput] = useState("");

  const [form, setForm] = useState({
    business_name: "",
    tagline: "",
    bio: "",
    vendor_type: "food",
    location: "",
    location_lat: null,
    location_lng: null,
    avatar_url: "",
    banner_url: "",
    cuisine_tags: [],
    phone: "",
    instagram: "",
    tiktok: "",
    website: "",
    delivery_radius_km: "",
    accepting_orders: true,
    location_privacy_mode: "public",
    hours: blankHours(),
    payment_handles: { cashapp: "", venmo: "", zelle: "", paypal: "", apple_pay: "", in_person: "" },
  });

  // Hydrate from current user
  useEffect(() => {
    if (!user) return;
    const vp = user.vendor_profile || {};
    setForm((f) => ({
      ...f,
      ...vp,
      hours: { ...blankHours(), ...(vp.hours || {}) },
      payment_handles: { ...f.payment_handles, ...(vp.payment_handles || {}) },
      delivery_radius_km: vp.delivery_radius_km != null ? String(vp.delivery_radius_km) : "",
      cuisine_tags: Array.isArray(vp.cuisine_tags) ? vp.cuisine_tags : [],
      accepting_orders: vp.accepting_orders !== false,
      location_privacy_mode: vp.location_privacy_mode || "public",
    }));
    setLoaded(true);
  }, [user]);

  if (!user) return null;
  if (user && !user.is_vendor) {
    return (
      <div className="max-w-xl mx-auto px-4 py-10 text-center">
        <h1 className="text-3xl font-black tracking-tighter">Open a shop first</h1>
        <p className="text-zinc-400 mt-2">You'll be able to edit your shop the moment you become a vendor.</p>
        <button
          onClick={() => navigate("/sell")}
          className="mt-4 h-11 px-5 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
          data-testid="open-sell"
        >
          Open Sell
        </button>
      </div>
    );
  }

  const toggleCuisine = (val) => {
    setForm((f) => {
      const set = new Set(f.cuisine_tags || []);
      set.has(val) ? set.delete(val) : set.add(val);
      return { ...f, cuisine_tags: Array.from(set) };
    });
  };

  const addCustomTag = () => {
    const slug = slugifyCategory(customTagInput);
    if (!slug) return toast.error("Type a category name first");
    if (slug.length < 2) return toast.error("Category name is too short");
    setForm((f) => {
      if ((f.cuisine_tags || []).includes(slug)) {
        toast.error("You already added that one");
        return f;
      }
      return { ...f, cuisine_tags: [...(f.cuisine_tags || []), slug] };
    });
    setCustomTagInput("");
  };

  // Vendor-entered custom tags (anything not in preset list)
  const customTags = (form.cuisine_tags || []).filter((t) => !isPresetCategory(t));

  const uploadOne = async (file, kind) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8 MB");
      return;
    }
    const setter = kind === "avatar" ? setUploadingAvatar : setUploadingBanner;
    setter(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [`${kind}_url`]: data.path }));
      toast.success(kind === "avatar" ? "Profile photo updated" : "Banner uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setter(false);
    }
  };

  const fillFromBrowserLocation = async () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported on this device");
    setBusyLoc(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, location_lat: pos.coords.latitude, location_lng: pos.coords.longitude }));
        setBusyLoc(false);
        toast.success("Pinned to your current spot");
      },
      (err) => { setBusyLoc(false); toast.error(err.message || "Couldn't read location"); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const setHandle = (k, v) =>
    setForm((f) => ({ ...f, payment_handles: { ...f.payment_handles, [k]: v } }));

  const setHour = (day, field, value) =>
    setForm((f) => ({ ...f, hours: { ...f.hours, [day]: { ...f.hours[day], [field]: value } } }));

  const copyMondayHours = () => {
    setForm((f) => {
      const mon = f.hours.mon || { open: "", close: "", closed: false };
      const next = { ...f.hours };
      WEEKDAYS.forEach((d) => { if (d.key !== "mon") next[d.key] = { ...mon }; });
      return { ...f, hours: next };
    });
    toast.success("Copied Monday's hours to every weekday");
  };

  const save = async () => {
    if (!form.business_name?.trim()) return toast.error("Shop name is required");
    if (form.tagline && form.tagline.length > 120) return toast.error("Tagline is too long (120 char max)");
    setSaving(true);
    try {
      const payload = {
        ...form,
        delivery_radius_km: form.delivery_radius_km !== "" ? parseFloat(form.delivery_radius_km) : null,
      };
      await api.put("/vendors/profile", payload);
      await refreshUser();
      toast.success("Shop updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save shop");
    } finally {
      setSaving(false);
    }
  };

  const removeBanner = () => setForm((f) => ({ ...f, banner_url: "" }));

  return (
    <div className="max-w-3xl mx-auto px-4 py-5 pb-24 space-y-5" data-testid="vendor-shop-edit">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="shop-edit-back">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <Button
          onClick={save}
          disabled={saving}
          className="h-11 px-5 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
          data-testid="shop-save-top"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
          Save
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-black tracking-tighter">Edit shop</h1>
        <p className="text-sm text-zinc-400 mt-1">Update anything anytime — saves instantly, customers see changes the next time they open your shop.</p>
      </div>

      {/* Shortcut to Map Pin customization (Phase 7) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          onClick={() => navigate("/shop/map-pin")}
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-pink-400/40 bg-gradient-to-r from-pink-500/10 via-zinc-950 to-cyan-500/10 p-4 hover:border-pink-400/70 transition-colors"
          data-testid="shop-open-map-pin"
        >
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-pink-300 font-black">Shop customization</p>
            <p className="text-base font-black tracking-tight">My Map Pin</p>
            <p className="text-xs text-zinc-400 mt-0.5">Earn premium markers as your reputation grows.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-pink-300" />
        </button>
        <button
          onClick={() => user?.user_id && navigate(`/shop/shop_${user.user_id}`)}
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-cyan-400/40 bg-gradient-to-r from-cyan-500/10 via-zinc-950 to-pink-500/10 p-4 hover:border-cyan-400/70 transition-colors"
          data-testid="shop-open-public-view"
        >
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">Preview</p>
            <p className="text-base font-black tracking-tight">View public storefront</p>
            <p className="text-xs text-zinc-400 mt-0.5">See exactly what customers see.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-cyan-300" />
        </button>
        <button
          onClick={() => navigate("/shop/team")}
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-cyan-400/40 bg-white/[0.03] p-4 hover:border-cyan-400/70 transition-colors"
          data-testid="shop-open-team"
        >
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">Team</p>
            <p className="text-base font-black tracking-tight">Invite & manage staff</p>
            <p className="text-xs text-zinc-400 mt-0.5">Invitations, roles, ownership transfer.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-cyan-300" />
        </button>
        <button
          onClick={() => navigate("/shop/credentials")}
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-emerald-400/40 bg-white/[0.03] p-4 hover:border-emerald-400/70 transition-colors"
          data-testid="shop-open-credentials"
        >
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">Trust</p>
            <p className="text-base font-black tracking-tight">Credentials & verification</p>
            <p className="text-xs text-zinc-400 mt-0.5">Upload permits, manage public badges.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-emerald-300" />
        </button>
        <button
          onClick={() => navigate("/shop/promotions")}
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-amber-400/40 bg-white/[0.03] p-4 hover:border-amber-400/70 transition-colors"
          data-testid="shop-open-promotions"
        >
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black">Marketing</p>
            <p className="text-base font-black tracking-tight">Coupons, deals & offers</p>
            <p className="text-xs text-zinc-400 mt-0.5">Codes, auto-discounts, BOGO, scheduling.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-amber-300" />
        </button>
        <button
          onClick={() => navigate("/shop/loyalty")}
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-amber-400/40 bg-white/[0.03] p-4 hover:border-amber-400/70 transition-colors"
          data-testid="shop-open-loyalty"
        >
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black">Marketing</p>
            <p className="text-base font-black tracking-tight">Loyalty rewards</p>
            <p className="text-xs text-zinc-400 mt-0.5">Give regulars points they can spend only with you.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-amber-300" />
        </button>
        <button
          onClick={() => navigate("/shop/menu-builder")}
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-pink-400/40 bg-white/[0.03] p-4 hover:border-pink-400/70 transition-colors"
          data-testid="shop-open-menu-builder"
        >
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-pink-300 font-black">Menu 3.0</p>
            <p className="text-base font-black tracking-tight">Menu builder</p>
            <p className="text-xs text-zinc-400 mt-0.5">Categories, ingredients, modifier groups.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-pink-300" />
        </button>
        <button
          onClick={() => navigate("/shop/order-center")}
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-emerald-400/40 bg-white/[0.03] p-4 hover:border-emerald-400/70 transition-colors"
          data-testid="shop-open-order-center"
        >
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">Live board</p>
            <p className="text-base font-black tracking-tight">Order center</p>
            <p className="text-xs text-zinc-400 mt-0.5">Accept, prep, ready, refund — everything the kitchen needs.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-emerald-300" />
        </button>
        <button
          onClick={() => navigate("/shop/issues")}
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-red-400/40 bg-white/[0.03] p-4 hover:border-red-400/70 transition-colors"
          data-testid="shop-open-issues"
        >
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-red-300 font-black">Issues</p>
            <p className="text-base font-black tracking-tight">Issue inbox</p>
            <p className="text-xs text-zinc-400 mt-0.5">Resolve complaints with full order context and refund in one tap.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-red-300" />
        </button>
        <button
          onClick={() => navigate("/shop/locations")}
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-sky-400/40 bg-white/[0.03] p-4 hover:border-sky-400/70 transition-colors"
          data-testid="shop-open-locations"
        >
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-sky-300 font-black">Multi-location</p>
            <p className="text-base font-black tracking-tight">Locations</p>
            <p className="text-xs text-zinc-400 mt-0.5">Add branches — customers pick one before ordering.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-sky-300" />
        </button>
        <button
          onClick={() => navigate("/driver")}
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-violet-400/40 bg-white/[0.03] p-4 hover:border-violet-400/70 transition-colors"
          data-testid="shop-open-driver"
        >
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-violet-300 font-black">Delivery</p>
            <p className="text-base font-black tracking-tight">Driver view</p>
            <p className="text-xs text-zinc-400 mt-0.5">Pick up and mark delivered. Assign drivers from the order center.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-violet-300" />
        </button>
      </div>

      {/* Banner + Avatar */}
      <Section title="Storefront photos" subtitle="Banner is shown at the top of your shop. Profile photo is what buyers see in the feed." testid="shop-photos">
        {/* Banner */}
        <div>
          <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Banner (3:1)</Label>
          <div className="mt-2 relative rounded-2xl overflow-hidden aspect-[3/1] bg-gradient-to-br from-pink-500/10 to-cyan-500/10 border border-white/10 grid place-items-center">
            {form.banner_url ? (
              <>
                <img src={fileUrl(form.banner_url)} alt="banner" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={removeBanner}
                  className="absolute top-2 right-2 w-8 h-8 grid place-items-center rounded-full bg-black/60 hover:bg-black/80 text-white"
                  aria-label="Remove banner"
                  data-testid="remove-banner"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            ) : (
              <label className="cursor-pointer text-center text-zinc-400 hover:text-white">
                <ImagePlus className="w-7 h-7 mx-auto mb-1" />
                <p className="text-sm font-bold">Upload banner</p>
                <p className="text-[10px] uppercase tracking-widest">Recommended 1500×500</p>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadOne(e.target.files?.[0], "banner")} data-testid="banner-upload-input" />
              </label>
            )}
          </div>
          {uploadingBanner && <p className="text-xs text-zinc-400 mt-1.5">Uploading banner…</p>}
        </div>
        {/* Avatar */}
        <div>
          <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Profile photo</Label>
          <div className="mt-2 flex items-center gap-4">
            <label className="relative w-20 h-20 rounded-full bg-white/5 border-2 border-dashed border-white/15 hover:border-pink-400 cursor-pointer overflow-hidden grid place-items-center transition-colors" data-testid="avatar-upload-label">
              {form.avatar_url ? (
                <img src={fileUrl(form.avatar_url)} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-6 h-6 text-zinc-400" />
              )}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadOne(e.target.files?.[0], "avatar")} data-testid="avatar-upload-input" />
            </label>
            <p className="text-xs text-zinc-400">{uploadingAvatar ? "Uploading…" : "Tap to replace your profile photo"}</p>
          </div>
        </div>
      </Section>

      {/* Identity */}
      <Section title="Shop identity" testid="shop-identity">
        <div className="space-y-1.5">
          <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Shop name</Label>
          <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="h-12 bg-white/5 border-white/10 rounded-2xl text-white" placeholder="Mama Lupe's Tacos" data-testid="shop-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Tagline · 1 line</Label>
          <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} maxLength={120} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" placeholder="Real Mexican home cooking, every Friday night" data-testid="shop-tagline-input" />
          <p className="text-[10px] text-zinc-500">{(form.tagline || "").length}/120</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">About your shop</Label>
          <Textarea
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            rows={5}
            className="bg-white/5 border-white/10 rounded-2xl text-white"
            placeholder="Tell your story. What you cook, where you grew up cooking it, who you cook for."
            data-testid="shop-bio-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Shop type</Label>
          <div className="flex gap-2">
            {[{v:"food",l:"Food",Icon:Utensils},{v:"wholesale",l:"Wholesale",Icon:Package}].map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => setForm({ ...form, vendor_type: o.v })}
                className={`h-10 px-4 rounded-full text-sm font-bold border inline-flex items-center gap-1.5 ${
                  form.vendor_type === o.v ? "bg-pink-500/20 text-pink-200 border-pink-400/40" : "bg-white/5 text-zinc-300 border-white/10"
                }`}
                data-testid={`vendor-type-${o.v}`}
              >
                <o.Icon className="w-4 h-4" />
                {o.l}
              </button>
            ))}
          </div>
        </div>
      </Section>

      {/* Categories */}
      <Section title="Categories you cook in" subtitle="Pick what fits — or add your own. These help buyers find you." testid="shop-categories">
        <div className="flex flex-wrap gap-2">
          {CATEGORY_OPTIONS.map((c) => {
            const on = form.cuisine_tags?.includes(c.value);
            return (
              <button
                key={c.value}
                type="button"
                onClick={() => toggleCuisine(c.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-bold border transition-colors inline-flex items-center gap-1.5 ${
                  on ? "bg-pink-500/25 text-pink-100 border-pink-400/60" : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"
                }`}
                data-testid={`cuisine-tag-${c.value}`}
              >
                <CategoryIcon slug={c.icon} size={13} strokeWidth={2.2} />
                {c.name}
              </button>
            );
          })}
        </div>

        {customTags.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-2">Your custom tags</p>
            <div className="flex flex-wrap gap-2">
              {customTags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold bg-amber-400/15 text-amber-100 border border-amber-400/40"
                  data-testid={`custom-tag-${t}`}
                >
                  {formatCategoryLabel(t)}
                  <button
                    type="button"
                    onClick={() => toggleCuisine(t)}
                    className="opacity-70 hover:opacity-100"
                    aria-label={`Remove ${t}`}
                    data-testid={`remove-custom-tag-${t}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 items-stretch pt-1">
          <Input
            value={customTagInput}
            onChange={(e) => setCustomTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomTag(); } }}
            placeholder="Add your own (e.g. Spicy Korean Tacos)"
            maxLength={40}
            className="h-11 bg-white/5 border-white/10 rounded-2xl text-white"
            data-testid="custom-tag-input"
          />
          <button
            type="button"
            onClick={addCustomTag}
            className="shrink-0 h-11 px-4 rounded-2xl bg-pink-500 hover:bg-pink-400 text-white font-bold text-sm glow-pink"
            data-testid="add-custom-tag-btn"
          >
            + Add
          </button>
        </div>
      </Section>

      {/* Location */}
      <Section title="Where you're based" subtitle="Helps buyers within your delivery radius discover you." testid="shop-location">
        <div className="space-y-1.5">
          <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Neighborhood or city</Label>
          <div className="relative">
            <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="pl-9 h-11 bg-white/5 border-white/10 rounded-2xl text-white" placeholder="Bushwick, Brooklyn" data-testid="shop-location-input" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Latitude</Label>
            <Input value={form.location_lat ?? ""} onChange={(e) => setForm({ ...form, location_lat: e.target.value === "" ? null : parseFloat(e.target.value) })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" placeholder="40.7128" data-testid="shop-lat-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Longitude</Label>
            <Input value={form.location_lng ?? ""} onChange={(e) => setForm({ ...form, location_lng: e.target.value === "" ? null : parseFloat(e.target.value) })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" placeholder="-74.0060" data-testid="shop-lng-input" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fillFromBrowserLocation} disabled={busyLoc} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white/10 hover:bg-white/15 text-white font-bold text-sm disabled:opacity-50" data-testid="use-my-location">
            <LocateFixed className="w-4 h-4" /> {busyLoc ? "Locating…" : "Use my current location"}
          </button>
        </div>
        <div className="space-y-1.5">
          <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Delivery radius (km)</Label>
          <Input type="number" min="0" step="0.5" value={form.delivery_radius_km} onChange={(e) => setForm({ ...form, delivery_radius_km: e.target.value })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" placeholder="5" data-testid="shop-radius-input" />
          <p className="text-[10px] text-zinc-500">Leave empty to allow nationwide pickup orders</p>
        </div>
      </Section>

      {/* Phase 8 — Location Privacy */}
      <Section
        title="Location privacy"
        subtitle="Control how much of your address customers can see on the map. You can change this any time."
        testid="shop-location-privacy"
      >
        <div className="grid grid-cols-1 gap-2">
          {[
            {
              key: "public",
              title: "Public storefront",
              desc: "Show my exact location to everyone. Best for restaurants and permanent shops.",
              preview: "Customers see the true pin.",
            },
            {
              key: "pickup",
              title: "Pickup location",
              desc: "Show my public pickup point. Same as Public for now; reserved for future pickup workflows.",
              preview: "Customers see the pickup pin.",
            },
            {
              key: "approximate",
              title: "Approximate area",
              desc: "Hide my exact spot. Only show a general area so I appear in nearby searches without revealing where I actually live/cook.",
              preview: "Customers see a pin within about 300m of your true spot — the same nearby spot every refresh.",
            },
            {
              key: "exact_on_order",
              title: "Exact location only after order",
              desc: "Approximate for everyone by default. Customers who have a paid order with me automatically see the exact address.",
              preview: "Browsers see an approximate area. Paying customers see the true pin.",
            },
          ].map((o) => (
            <label
              key={o.key}
              className={`rounded-2xl border p-3 flex items-start gap-3 cursor-pointer transition-colors ${
                form.location_privacy_mode === o.key
                  ? "border-pink-400/50 bg-pink-500/10"
                  : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
              }`}
              data-testid={`shop-privacy-${o.key}`}
            >
              <input
                type="radio"
                name="location_privacy_mode"
                checked={form.location_privacy_mode === o.key}
                onChange={() => setForm({ ...form, location_privacy_mode: o.key })}
                className="mt-1 accent-pink-500"
                data-testid={`shop-privacy-radio-${o.key}`}
              />
              <div className="min-w-0">
                <p className="text-sm font-black tracking-tight">{o.title}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{o.desc}</p>
                <p className="text-[11px] text-cyan-300/90 mt-1.5">Preview: {o.preview}</p>
              </div>
            </label>
          ))}
        </div>
      </Section>

      {/* Hours */}
      <Section title="Open hours" subtitle="Set the hours buyers can place orders. Mark closed days." testid="shop-hours">
        <button type="button" onClick={copyMondayHours} className="h-9 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold inline-flex items-center gap-1.5" data-testid="copy-monday">
          <Clock className="w-3 h-3" /> Copy Monday → all other days
        </button>
        <div className="space-y-2">
          {WEEKDAYS.map((d) => {
            const row = form.hours?.[d.key] || { open: "", close: "", closed: false };
            return (
              <div key={d.key} className="grid grid-cols-[60px_1fr_1fr_auto] items-center gap-2" data-testid={`hours-row-${d.key}`}>
                <span className="text-sm font-bold text-zinc-300">{d.label}</span>
                <Input type="time" disabled={row.closed} value={row.open || ""} onChange={(e) => setHour(d.key, "open", e.target.value)} className="h-10 bg-white/5 border-white/10 rounded-xl text-white disabled:opacity-50" />
                <Input type="time" disabled={row.closed} value={row.close || ""} onChange={(e) => setHour(d.key, "close", e.target.value)} className="h-10 bg-white/5 border-white/10 rounded-xl text-white disabled:opacity-50" />
                <button type="button" onClick={() => setHour(d.key, "closed", !row.closed)} className={`h-10 px-3 rounded-xl text-xs font-bold ${row.closed ? "bg-red-500/15 text-red-200 border border-red-400/40" : "bg-white/5 text-zinc-300 border border-white/10"}`}>
                  {row.closed ? "Closed" : "Open"}
                </button>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Contact + Social */}
      <Section title="Contact & socials" subtitle="Optional, but the more buyers can reach you, the more orders you'll get." testid="shop-contact">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Phone</Label>
            <div className="relative">
              <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="pl-9 h-11 bg-white/5 border-white/10 rounded-2xl text-white" placeholder="(555) 555-1234" data-testid="shop-phone-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Website</Label>
            <div className="relative">
              <Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="pl-9 h-11 bg-white/5 border-white/10 rounded-2xl text-white" placeholder="yoursite.com" data-testid="shop-website-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Instagram</Label>
            <div className="relative">
              <Instagram className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value.replace(/^@/, "") })} className="pl-9 h-11 bg-white/5 border-white/10 rounded-2xl text-white" placeholder="yourhandle" data-testid="shop-instagram-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">TikTok</Label>
            <div className="relative">
              <Music2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input value={form.tiktok} onChange={(e) => setForm({ ...form, tiktok: e.target.value.replace(/^@/, "") })} className="pl-9 h-11 bg-white/5 border-white/10 rounded-2xl text-white" placeholder="yourhandle" data-testid="shop-tiktok-input" />
            </div>
          </div>
        </div>
      </Section>

      {/* Payments */}
      <Section title="How buyers pay you" subtitle="Hidden Gem never takes a cut. Buyers pay you directly through these handles." testid="shop-payments">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { k: "cashapp", l: "Cash App", ph: "$yourhandle" },
            { k: "zelle", l: "Zelle", ph: "(555) 555-1234 or email" },
            { k: "venmo", l: "Venmo", ph: "@yourhandle" },
            { k: "paypal", l: "PayPal", ph: "you@email.com" },
            { k: "apple_pay", l: "Apple Pay", ph: "(555) 555-1234 / email" },
            { k: "in_person", l: "In Person", ph: "Cash on pickup OK" },
          ].map((p) => (
            <div key={p.k} className="space-y-1.5">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">{p.l}</Label>
              <Input value={form.payment_handles?.[p.k] || ""} onChange={(e) => setHandle(p.k, e.target.value)} placeholder={p.ph} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid={`pay-${p.k}-input`} />
            </div>
          ))}
        </div>
      </Section>

      {/* Open/Close switch */}
      <Section title="Shop status" subtitle="Pause your shop temporarily if you're out of stock or on vacation." testid="shop-status">
        <button
          type="button"
          onClick={() => setForm({ ...form, accepting_orders: !form.accepting_orders })}
          className={`w-full h-14 px-5 rounded-2xl border flex items-center justify-between font-bold ${
            form.accepting_orders ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-100" : "bg-amber-500/15 border-amber-400/40 text-amber-100"
          }`}
          data-testid="accepting-orders-toggle"
        >
          <span>{form.accepting_orders ? "Accepting orders" : "Paused (not accepting orders)"}</span>
          {form.accepting_orders ? <ToggleRight className="w-7 h-7" /> : <ToggleLeft className="w-7 h-7" />}
        </button>
      </Section>

      {/* Sticky save bar */}
      <div className="sticky bottom-3 z-10">
        <Button
          onClick={save}
          disabled={saving}
          className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
          data-testid="shop-save-bottom"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save shop
        </Button>
      </div>
    </div>
  );
}
